import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { PageHeader } from '@/components/dashboard/page-header';
import { DataTable, StatusBadge, type DataTableColumn } from '@/components/data-table';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_admin/admin/scoring')({
  component: ScoringPage,
});

const THRESHOLD = 40;

interface BidRow {
  id: string;
  profileScore: number | null;
  linguisticScore: number | null;
  status: string;
  createdAt: Date;
  offer: { id: string; title: string };
  bidder: { id: string; email: string; displayName: string | null };
}

const COLUMNS: DataTableColumn<BidRow>[] = [
  {
    key: 'offer',
    header: 'Offre',
    cell: (row) => <span className="font-medium">{row.offer.title}</span>,
  },
  {
    key: 'bidder',
    header: 'Compte',
    cell: (row) => <span className="text-xs">{row.bidder.email}</span>,
  },
  {
    key: 'profile',
    header: 'Score profil',
    align: 'right',
    cell: (row) => <span className="font-mono tabular-nums">{row.profileScore ?? '—'}</span>,
  },
  {
    key: 'ling',
    header: 'Linguistique',
    align: 'right',
    cell: (row) => <span className="font-mono tabular-nums">{row.linguisticScore ?? '—'}</span>,
  },
  {
    key: 'status',
    header: 'Statut',
    cell: (row) => <StatusBadge tone="neutral" label={row.status} />,
  },
];

function ScoringPage(): React.ReactElement {
  const list = trpc.admin.scoringList.useQuery({ threshold: THRESHOLD, limit: 50 });
  const rows = (list.data ?? []) as BidRow[];

  const [done, setDone] = React.useState<Set<string>>(new Set());
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const rescore = trpc.admin.rescoreBid.useMutation({
    onSuccess: (_, vars) => {
      setDone((prev) => new Set([...prev, vars.bidId]));
    },
    onError: (err, vars) => {
      setErrors((prev) => ({ ...prev, [vars.bidId]: err.message }));
    },
  });

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
      <PageHeader
        title="Scoring IA"
        subtitle={`Candidatures sans score ou sous le seuil de ${THRESHOLD}.`}
      />
      <DataTable
        columns={COLUMNS}
        rows={rows}
        rowKey={(row) => row.id}
        actionHeader="Action"
        rowAction={(row) =>
          done.has(row.id) ? (
            <span className="text-success text-xs font-medium">Job enfilé</span>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                aria-label={`Rescorer la candidature de ${row.bidder.email}`}
                disabled={rescore.isPending}
                onClick={() => rescore.mutate({ bidId: row.id })}
                className="bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-50"
              >
                {rescore.isPending ? 'En cours…' : 'Rescorer'}
              </button>
              {errors[row.id] ? <p className="text-destructive text-xs">{errors[row.id]}</p> : null}
            </div>
          )
        }
        emptyState={
          <p className="text-muted-foreground text-center text-sm">
            Aucune candidature à rescorer.
          </p>
        }
      />
    </div>
  );
}
